import { useCallback, useEffect, useMemo, useState } from 'react';
import { toApiError } from '../../api/errors';
import { ErrorNotice, Notice, StaleBanner } from '../../components/ui/Notice';
import { SkeletonBar } from '../../components/ui/Skeleton';
import { useResource } from '../../hooks/useResource';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import type { LaneDef, StatusDef } from '../../api/types';
import {
  fetchTransitionMatrix,
  refusalReason,
  replaceTransitionMatrix,
  writeRouteMissing,
  type TransitionEdge,
} from './settings-api';
import {
  applyTargets,
  groupByLane,
  inboundCounts,
  outboundCounts,
  permittedSummary,
  targetsChanged,
  targetsFor,
} from './transitions-model';
import { canWrite, disabledReason, type WriteGate } from './WriteGate';

/**
 * 🔴🔴 THE TRANSITION MATRIX, AND IT IS NOT A MATRIX ON SCREEN.
 *
 * 26 statuses is 676 cells. A 26×26 grid of checkboxes needs both axes labelled to be readable at
 * all, does not fit on a desktop let alone a 390px phone, and answers a question nobody asks.
 *
 * ⭐⭐ The question people actually ask is **"from Code Review, where may this go?"** — which is ONE
 * ROW of that grid. So this shows one `from` status at a time with a checklist of permitted targets,
 * grouped by lane. ⭐ Being a single subject with a list of answers, it has no second axis to lose,
 * which is why it is the SAME layout on a phone and on a 1280px desktop rather than a shrunken one.
 *
 * ⛔ It never renders a grid at any breakpoint. A horizontally-scrolling 676-cell table is
 * technically responsive and practically unusable — the same mistake the README rules out for the
 * board.
 */
export function TransitionsPanel({ gate }: { gate: WriteGate }) {
  const vocab = useVocabulary();
  const matrix = useResource<TransitionEdge[]>((signal) => fetchTransitionMatrix(signal), []);

  const statuses = vocab.statuses as StatusDef[];
  const lanes = (vocab.resource.data?.lanes ?? []) as LaneDef[];

  /**
   * ⚠️ ONLY ACTIVE STATUSES ARE OFFERED AS TARGETS — and `readTransitionMatrix` already filters both
   * ends by `is_active`, so an edge touching a retired status is not in what we read either. The
   * denominator on screen is this number, not 26; printing 26 would make a complete row look
   * incomplete.
   */
  const selectable = useMemo(() => statuses.filter((status) => status.is_active), [statuses]);
  const [from, setFrom] = useState<string>('');
  /**
   * 🔴 THE EDIT IN PROGRESS, TAGGED WITH THE STATUS IT BELONGS TO — and the checked set is DERIVED
   * during render rather than copied into state by an effect.
   *
   * ⭐⭐ The effect version had a real defect that a flaky test caught: for one paint after the
   * matrix arrived, `targets` was still `[]` while `from` was already set, so "12 permitted" rendered
   * as zero checkboxes AND the Save button was enabled — a save in that instant would have deleted
   * every edge from that status. Deriving it means the empty state never exists.
   *
   * ⛔ Tagging by `from` is what stops one status's edits being carried onto another when the picker
   * changes; a plain `string[]` cannot tell whose edit it is.
   */
  const [draft, setDraft] = useState<{ from: string; list: string[] } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const edges = useMemo(() => matrix.data ?? [], [matrix.data]);
  const outbound = useMemo(() => outboundCounts(edges), [edges]);
  const inbound = useMemo(() => inboundCounts(edges), [edges]);

  // Default to the initial status — "where can a brand new ticket go" is the first question asked.
  useEffect(() => {
    if (from || selectable.length === 0) return;
    const initial = selectable.find((status) => status.is_initial) ?? selectable[0];
    if (initial) setFrom(initial.code);
  }, [from, selectable]);

  /**
   * ⭐ The server's answer unless this exact status is being edited. No effect, no copy, no window in
   * which the two disagree.
   */
  const targets = useMemo(
    () => (draft && draft.from === from ? draft.list : targetsFor(edges, from)),
    [draft, from, edges],
  );

  const dirty = from ? targetsChanged(edges, from, targets) : false;
  const editable = canWrite(gate);
  const groups = useMemo(() => groupByLane(selectable, lanes), [selectable, lanes]);

  const toggle = useCallback(
    (code: string) => {
      const next = targets.includes(code)
        ? targets.filter((entry) => entry !== code)
        : [...targets, code];
      setDraft({ from, list: next });
      setSaved(false);
    },
    [targets, from],
  );

  const save = useCallback(async () => {
    if (!from) return;
    setSaving(true);
    setSaveError(null);
    try {
      /**
       * 🔴 THE WHOLE MATRIX. `PUT /settings/transitions` REPLACES it, so the payload carries every
       * other status's rules unchanged — `applyTargets` is the only thing allowed to build it, and it
       * is unit-tested for exactly this. Sending just this row's edges would delete the workflow.
       */
      await replaceTransitionMatrix(applyTargets(edges, from, targets));
      setSaved(true);
      // ⭐ The draft is dropped so what is on screen is once again the SERVER's answer — keeping it
      // would leave the checkboxes showing our intention even if the reload disagreed.
      setDraft(null);
      matrix.reload();
    } catch (err) {
      const refusal = refusalReason(err);
      setSaveError(
        refusal ??
          (writeRouteMissing(err)
            ? 'The API answered "no such route" — PUT /api/v1/settings/transitions is not deployed.'
            : describe(err)),
      );
    } finally {
      setSaving(false);
    }
  }, [from, edges, targets, matrix]);

  const fromStatus = selectable.find((status) => status.code === from) ?? null;

  return (
    <section aria-label="Transitions" className="min-w-0">
      <header className="px-3 py-3 sm:px-4">
        <h2 className="text-base font-semibold">Transitions</h2>
        <p className="mt-0.5 text-sm text-[color:var(--color-ink-muted)]">
          Which statuses each status may move to. {selectable.length} statuses is{' '}
          {selectable.length * selectable.length} possible edges — asked one "from" at a time, which is
          the question people actually have.
        </p>
      </header>

      <div className="space-y-3 px-3 pb-3 sm:px-4">
        {matrix.error && !matrix.isStale ? (
          <ErrorNotice error={matrix.error} onRetry={matrix.reload} />
        ) : null}
        {matrix.isStale && matrix.error ? (
          <StaleBanner error={matrix.error} loadedAt={matrix.loadedAt} onRetry={matrix.reload} />
        ) : null}
        {vocab.resource.error && !vocab.resource.isStale ? (
          <ErrorNotice error={vocab.resource.error} onRetry={vocab.resource.reload} />
        ) : null}

        {matrix.isLoading ? <SkeletonBar className="h-64 w-full" /> : null}
      </div>

      {!matrix.isLoading && matrix.data ? (
        <div className="px-3 pb-6 sm:px-4">
          <label className="block text-xs font-bold tracking-wide uppercase" htmlFor="transition-from">
            From
          </label>
          {/*
            ⭐ A SELECT, on every breakpoint. 26 options in a native picker is one tap on a phone and
            one click on a desktop; a 26-item sidebar is neither.
          */}
          <select
            id="transition-from"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              // ⛔ An unsaved edit does not follow the picker. It belonged to the previous status.
              setDraft(null);
              setSaved(false);
              setSaveError(null);
            }}
            className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-2 text-sm sm:max-w-md"
          >
            {selectable.map((status) => (
              <option key={status.code} value={status.code}>
                {status.label} ({outbound.get(status.code) ?? 0} out · {inbound.get(status.code) ?? 0}{' '}
                in)
              </option>
            ))}
          </select>

          {fromStatus ? (
            <p className="mt-2 text-sm">
              <strong>{fromStatus.label}</strong> may move to{' '}
              <strong>{permittedSummary(targets, selectable.length)}</strong>
              {fromStatus.is_final ? ' — this is a final status.' : '.'}
            </p>
          ) : null}

          {targets.length === 0 && fromStatus ? (
            <div className="mt-2">
              <Notice tone="warning" title="Nothing is permitted from here">
                <p>
                  A ticket that reaches <code>{fromStatus.code}</code> cannot leave it.
                  {fromStatus.is_final
                    ? ' That is expected for a final status.'
                    : ' That is a dead end, and this status is not marked final.'}
                </p>
              </Notice>
            </div>
          ) : null}

          <fieldset className="mt-4">
            <legend className="text-xs font-bold tracking-wide uppercase">
              Permitted targets, by lane
            </legend>
            <div className="mt-2 space-y-4">
              {groups.map((group) => {
                const checkedHere = group.statuses.filter((status) =>
                  targets.includes(status.code),
                ).length;
                return (
                  <div key={group.lane.code}>
                    <h3 className="text-xs font-semibold text-[color:var(--color-ink-muted)]">
                      {group.lane.label}{' '}
                      <span className="font-normal">
                        — {checkedHere} of {group.statuses.length}
                      </span>
                    </h3>
                    <ul className="mt-1 divide-y divide-[color:var(--color-line)] rounded border border-[color:var(--color-line)]">
                      {group.statuses.map((status) => {
                        const checked = targets.includes(status.code);
                        return (
                          <li key={status.code}>
                            <label className="flex items-center gap-3 px-3 py-2.5 text-sm">
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={!editable}
                                title={disabledReason(gate)}
                                onChange={() => toggle(status.code)}
                                className="size-4 shrink-0"
                              />
                              <span className="min-w-0 flex-1">
                                {status.label}
                                {status.code === from ? (
                                  <span className="ml-1.5 text-xs text-[color:var(--color-ink-muted)]">
                                    (same status)
                                  </span>
                                ) : null}
                              </span>
                              <code className="shrink-0 text-xs text-[color:var(--color-ink-muted)]">
                                {status.code}
                              </code>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </fieldset>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={!editable || !dirty || saving}
              title={disabledReason(gate)}
              onClick={() => void save()}
              className="h-9 rounded bg-[color:var(--color-accent)] px-4 text-sm font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50"
            >
              {saving ? 'Saving…' : `Save transitions from ${fromStatus?.label ?? ''}`}
            </button>
            {dirty ? (
              <button
                type="button"
                onClick={() => setDraft(null)}
                className="h-9 rounded border border-[color:var(--color-line)] px-3 text-sm"
              >
                Revert
              </button>
            ) : null}
            {saved && !dirty ? (
              <span role="status" className="text-sm text-[color:var(--color-ink-muted)]">
                Saved.
              </span>
            ) : null}
          </div>

          {saveError ? (
            <p
              role="alert"
              className="mt-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-900/60 dark:bg-red-950/50 dark:text-red-100"
            >
              {saveError}
            </p>
          ) : null}

          <p className="mt-3 text-xs text-[color:var(--color-ink-muted)]">
            ⚠️ Saving sends the WHOLE matrix — {edges.length} edges — because the endpoint replaces it.
            The other statuses' rules are carried through unchanged, and any note already on a
            permitted edge is preserved.
          </p>
        </div>
      ) : null}
    </section>
  );
}

/**
 * ✅ THIS USED TO CARRY A CORS HINT, AND THE HINT IS NOW WRONG — SO IT IS GONE.
 *
 * `PUT` was genuinely missing from the API's CORS allow-list, which made a healthy API produce
 * "Cannot reach the PlanHub API" here: a method the allow-list omits is refused at the PREFLIGHT, so
 * `fetch` rejects at the network layer and the failure is indistinguishable from an outage. It was
 * found in a real browser, reported, and fixed — `app.ts` now registers
 * `['GET','POST','PUT','PATCH','DELETE','OPTIONS']` and its test asserts the whole set rather than
 * PUT alone.
 *
 * ⛔ The hint is deleted rather than left in place "just in case". A remedy that no longer applies
 * sends the next reader to check something that is fine, which costs more than saying nothing — and
 * a stale note outlives everyone who knew it was stale.
 */
function describe(err: unknown): string {
  const apiError = toApiError(err, '/settings/transitions');
  return `${apiError.message}${apiError.reason ? ` — ${apiError.reason}` : ''}`;
}
